import React from 'react';
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { useHistory } from "react-router-dom";
import { useAuthProvider } from '@components/AuthProvider';
import { IIndexable } from '@utils/TypeHelpers';

//SORTING
export enum EnumSortDirection {
    Asc = 1,
    Desc = 2
}
export interface ISortField {
    name: string,
    direction?: EnumSortDirection,
}

export interface ISortFieldRequest {
    sortField: ISortField
}

export interface ISortFieldResponse {
    sortField: ISortField
}

//PAGGING
export interface IPaginationParams {
    totalCount: number,
    currentPage: number,
    rowsPerPage: number,
    hasMore: boolean
}

export interface IPaginationRequest {
    requestedPage: number,
    rowsPerPage: number
}
export interface IPaginationResponse {
    pagination: IPaginationParams
}

//RESULT
export type IDataRequest<TFilter extends object = object> = {
    filter: Partial<TFilter>,
    pagination: IPaginationRequest,
    sortField: ISortField | undefined
}

export interface IResultResponse<TResult> {
    result: TResult
}

export interface IJustificativaRequest {
    justificativa: string
}

export interface IAlterarStatusRequest<T> {
    novoStatus: T
}

export interface IEnumRecord<TEnum>{
    value: TEnum,
    description: string,
    inactive: boolean
}

export type IDataResponse<T> = IResultResponse<T> & IPaginationResponse & ISortFieldResponse;
export interface IReportResponse<TResult, TSummary> {
    data: IDataResponse<TResult>
    summary: TSummary

}

export default function useRequest () {
    const authProvider = useAuthProvider();
    const history = useHistory();

    const axiosInstance = React.useMemo(() => {
        const instance = axios.create({
            baseURL: process.env.NODE_ENV === "production" ? '/api' : 'https://localhost:44344',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authProvider?.state?.token || ""}`,
                'Accept': 'application/json',
                'Access-Control-Allow-Origin': '*',
                'Access-Control-Allow-Methods': 'GET, POST, PATCH, PUT, DELETE, OPTIONS',
                'Access-Control-Allow-Headers': 'Origin, Content-Type, X-Auth-Token',
                'Access-Control-Expose-Headers': 'X-Suggested-Filename',
                'Access-Control-Max-Age': '600'
            }        
        });

        instance.interceptors.request.use(config => {
            config.headers.Authorization = `Bearer ${authProvider?.state?.token || ""}`;
            return config;
        });

        instance.interceptors.response.use(
            response => response, 
            error => {
                if (error.response === undefined) {
                    return Promise.reject({
                        message: "Não foi possível alcançar o servidor."                        
                    });
                }
                else if (error.response.status === 401) {
                    authProvider.clearAuth();
                    history.push("login");
                    return Promise.reject({
                        message: "Autenticação requerida."                        
                    });
                }
                else {
                    return Promise.reject(error);
                }
            }
        );

        return instance;
    }, [authProvider, history]);

    const buildQueryString = React.useCallback((source: IIndexable, keyPrefix: string, result: URLSearchParams)  => {
        const keys = Object.keys(source);
        const l = keys.length;

        for (let i = 0; i < l; i++) {
            const localKey = keys[i];
            const key = `${keyPrefix}${localKey}`;
            const value = source[localKey];

            if (value !== null && value !== undefined) {
                if (!!value.getDate) {
                    result.append(key, (value as Date).toISOString())
                }
                if (Array.isArray(value)) {
                    value.forEach(x => {
                        if (x !== undefined && x !== null) {
                            result.append(key, x);
                        }
                    });
                }
                else if (typeof(value) === 'object') {
                    buildQueryString(value, `${key}.`, result);
                }
                else {
                    result.append(key, value);
                }
            }
        }

        return result;
    }, []);

    const get = React.useCallback(function<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig) {
        if (typeof(config?.params) === "object") {
            const params = config?.params || {};
            config.params = buildQueryString(params, "", new URLSearchParams());
        }

        return axiosInstance.get<T, R>(url, config);
    }, [axiosInstance, buildQueryString])

    const getData = React.useCallback(function <T = any, TFilter extends object = object, U = IDataResponse<T>, R = AxiosResponse<U>>(url: string, dataRequest: IDataRequest<TFilter>, config?: AxiosRequestConfig) {
        const _config: AxiosRequestConfig = {
            ...config,
            params: dataRequest
        }

        return get<U, R>(url, _config);
    }, [get]);

    const getReport = React.useCallback(function <TResult = any, TSummary = any, TFilter extends object = object, U = IReportResponse<TResult, TSummary>, R = AxiosResponse<U>>(url: string, dataRequest: IDataRequest<TFilter>, config?: AxiosRequestConfig) {
        const _config: AxiosRequestConfig = {
            ...config,
            params: dataRequest
        }

        return get<U, R>(url, _config);
    }, [get]);

    const download = React.useCallback(function <T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig) {
        const _config: AxiosRequestConfig = {
            ...config,
            responseType: 'blob'        
        };

        return get<T, R>(url, _config);
    }, [get]);

    const downloadPost = React.useCallback(function <T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: AxiosRequestConfig) {
        const _config: AxiosRequestConfig = {
            ...config,
            responseType: 'blob'        
        };

        return axiosInstance.post<T, R>(url, data, _config);
    }, [axiosInstance]);

    const upload = React.useCallback(function <T = any, R = AxiosResponse<T>>(url: string, data?: FormData, config?: AxiosRequestConfig): Promise<R> {
        const _config: AxiosRequestConfig = {
            ...config,
            headers: {
                ...config?.headers,
                'Content-Type': 'multipart/form-data'
            }
        }
        return axiosInstance.post<T, R>(url, data, _config);
    }, [axiosInstance]);

    return React.useMemo(() => ({
        post: axiosInstance.post, 
        get, 
        delete: axiosInstance.delete, 
        put: axiosInstance.put,
        patch: axiosInstance.patch, 
        axiosInstance, 
        download,
        downloadPost, 
        upload,
        getData,
        getReport
    }), [axiosInstance, download, get, getData, upload, getReport, downloadPost]);
}
